"use client"

import {
  useProfile,
  navigateWithThreadPreservation,
  DashboardSectionAction,
  DashboardSectionTitle,
  DashboardSectionSkeleton,
  PaneResponsiveCardGrid,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useOpportunities, type Opportunity } from '../../hooks/use-opportunities'
import { buildApplicationStatusMap, useMyApplications } from '../../hooks/use-my-applications'
import { useNavigateToCandidateJobDetail } from '../../hooks/use-navigate-to-candidate-job-detail'
import { CandidateOpportunityJobCard } from './candidate-opportunity-job-card'
import { useSavedOpportunities } from '../../hooks/use-saved-opportunities'
import { openApplicationApplyCanvas } from './open-application-apply-canvas'
import { hasActiveApplicationForOpportunity } from './job-application-trailing'
import { Briefcase } from 'lucide-react'

/** Featured job grid: 4, else 2, else 1 — never 3. */
function featuredJobsDashboardDisplayCount(total: number): number {
  if (total >= 4) return 4
  if (total === 3) return 2
  return total
}

/** Bumps when the UTC hour changes so Featured rotation refetches. */
function useUtcHourBucket(): number {
  const [hourBucket, setHourBucket] = useState(() => Math.floor(Date.now() / 3_600_000))

  useEffect(() => {
    const now = Date.now()
    const nextHourMs = (hourBucket + 1) * 3_600_000
    const delay = Math.max(nextHourMs - now + 50, 1_000)
    const timer = window.setTimeout(() => {
      setHourBucket(Math.floor(Date.now() / 3_600_000))
    }, delay)
    return () => window.clearTimeout(timer)
  }, [hourBucket])

  return hourBucket
}

export function CandidateDashboardFeaturedJobsSection() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const handleOpenJob = useNavigateToCandidateJobDetail()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const applyCanvasKeyRef = useRef(0)
  const { profile } = useProfile()
  const rotationHour = useUtcHourBucket()

  const featuredFilters = useMemo(
    () => ({
      listing_type: 'featured' as const,
      placement: 'candidate_home' as const,
      rotation_hour: rotationHour,
      page: 1,
      page_size: 4,
    }),
    [rotationHour],
  )
  const fallbackFilters = useMemo(
    () => ({ listing_type: 'all' as const, page: 1, page_size: 4 }),
    [],
  )

  const featuredQuery = useOpportunities(featuredFilters)
  const useFallback =
    !featuredQuery.isLoading && !featuredQuery.error && featuredQuery.opportunities.length === 0
  const fallbackQuery = useOpportunities(fallbackFilters, { enabled: useFallback })

  const showingFeatured = featuredQuery.opportunities.length > 0
  const opportunities = showingFeatured ? featuredQuery.opportunities : fallbackQuery.opportunities
  const isLoading = featuredQuery.isLoading || (useFallback && fallbackQuery.isLoading)
  const error = featuredQuery.error ?? fallbackQuery.error
  const sectionTitle = showingFeatured ? 'Featured jobs' : 'Open roles'

  const { applications, refetch: refetchApplications } = useMyApplications({
    enabled: !!profile?.id,
  })
  const { savedIds, toggleSave, savingOpportunityId } = useSavedOpportunities()

  const handleSaveToggle = useCallback(
    (opportunityId: string) => {
      toggleSave(opportunityId, savedIds.has(opportunityId))
    },
    [savedIds, toggleSave],
  )

  const applicationStatusMap = useMemo(
    () => buildApplicationStatusMap(applications),
    [applications],
  )

  const displayTotal = featuredJobsDashboardDisplayCount(opportunities.length)
  const displayedOpportunities = opportunities.slice(0, displayTotal)

  const handleApplyClick = useCallback(
    (opportunity: Opportunity) => {
      if (hasActiveApplicationForOpportunity(applicationStatusMap, opportunity.id)) return

      openApplicationApplyCanvas({
        opportunity,
        openCanvas,
        closeCanvas,
        applyCanvasKeyRef,
        onApplied: () => void refetchApplications(),
        keyPrefix: 'featured-apply',
      })
    },
    [applicationStatusMap, closeCanvas, openCanvas, refetchApplications],
  )

  const navigateAllJobs = () => {
    navigateWithThreadPreservation(navigate, '/candidate/jobs', searchParams)
  }

  const jobsContent =
    error ? (
      <div className="rounded-lg bg-destructive/5 px-4 py-6 text-center text-sm">
        <p className="font-medium text-destructive">Could not load jobs</p>
        <p className="mt-1 text-muted-foreground">{(error as Error).message}</p>
      </div>
    ) : opportunities.length === 0 && !isLoading ? (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-10 text-center">
        <Briefcase className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
        <p className="text-sm font-medium">No open roles yet</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          Check back soon — new jobs are added regularly.
        </p>
      </div>
    ) : (
      <PaneResponsiveCardGrid columnMode="pane-compact">
        {displayedOpportunities.map((opportunity) => (
          <CandidateOpportunityJobCard
            key={opportunity.id}
            className="h-full"
            opportunity={opportunity}
            application={applicationStatusMap[opportunity.id]}
            saved={savedIds.has(opportunity.id)}
            isSaving={savingOpportunityId === opportunity.id}
            onOpenJob={handleOpenJob}
            onSaveToggle={handleSaveToggle}
            onApply={handleApplyClick}
          />
        ))}
      </PaneResponsiveCardGrid>
    )

  if (isLoading && opportunities.length === 0) {
    return <DashboardSectionSkeleton cards={2} grid columnMode="pane-compact" />
  }

  return (
    <section className="space-y-4" aria-labelledby="dashboard-jobs-heading">
      <DashboardSectionTitle
        id="dashboard-jobs-heading"
        title={sectionTitle}
        action={<DashboardSectionAction onClick={navigateAllJobs}>View all jobs</DashboardSectionAction>}
      />
      <div className="space-y-4">{jobsContent}</div>
    </section>
  )
}
